/**
 * File upload pipeline — presigned URL upload flow.
 *
 * Reliability model (see AW-42 / AW-43):
 * - The GCS PUT is the only unbounded network op (raw XHR). It carries a
 *   *stall* timeout — aborted only when no upload progress arrives for
 *   GCS_PUT_STALL_TIMEOUT_MS, so genuinely slow-but-alive large uploads are
 *   never killed, but a dead connection can't hold a concurrency slot forever.
 * - Each network step (initiate / PUT / confirm) retries transient failures
 *   with exponential backoff. The PUT is re-sent to the *same* presigned URL —
 *   we never re-initiate after a row exists, which would dedup-collide and be
 *   misreported as a duplicate.
 */

import type { FileUpload } from '@/types/dataSource';

import { computeSHA256 } from '@/lib/hash';

import axios from 'axios';

import api from './api';

// ---------------------------------------------------------------------------
// Reliability tuning
// ---------------------------------------------------------------------------

/** Abort a GCS PUT if no upload progress arrives for this long (stall, not slow). */
const GCS_PUT_STALL_TIMEOUT_MS = 45_000;
/** Max attempts per network step (initiate / PUT / confirm) before giving up. */
const UPLOAD_MAX_ATTEMPTS = 3;
/** Base backoff between retry attempts (exponential, plus jitter). */
const UPLOAD_RETRY_BASE_DELAY_MS = 800;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InitiateUploadResponse {
  upload_url: string;
  file: FileUpload;
}

interface BlobReusedResponse {
  bytes_already_uploaded: true;
  file: FileUpload;
}

interface DuplicateFileResponse {
  duplicate: true;
  file: FileUpload;
}

export interface UploadResult {
  file: FileUpload;
  isDuplicate: boolean;
  /** Bytes were already in GCS — no PUT happened, but a new row was created. */
  bytesReused: boolean;
}

/**
 * Upload-pipeline error that carries enough context to decide retryability.
 * `transient` errors (5xx, network drop, stall) are retried; permanent ones
 * (4xx — unsupported type, bad request) fail fast and surface to the user.
 */
export class UploadError extends Error {
  readonly status?: number;
  readonly transient: boolean;

  constructor(message: string, opts: { status?: number; transient: boolean }) {
    super(message);
    this.name = 'UploadError';
    this.status = opts.status;
    this.transient = opts.transient;
  }
}

// ---------------------------------------------------------------------------
// Retry helpers
// ---------------------------------------------------------------------------

/** Classify an error as transient (worth retrying) vs permanent. */
function isTransientError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return false;
  if (err instanceof UploadError) return err.transient;
  if (axios.isAxiosError(err)) {
    // No response = the request never completed (network failure / timeout).
    if (!err.response) return true;
    const status = err.response.status;
    return status >= 500 || status === 429;
  }
  // Unknown shape: don't retry — fail fast and surface it.
  return false;
}

/** Abortable sleep used between retry attempts. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Upload aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Run `fn`, retrying transient failures with exponential backoff + jitter.
 * Aborts (user cancel) and permanent errors propagate immediately.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  signal?: AbortSignal,
  maxAttempts = UPLOAD_MAX_ATTEMPTS,
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isTransientError(err) || attempt === maxAttempts) throw err;
      const backoff = UPLOAD_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
      await sleep(backoff + Math.random() * backoff * 0.25, signal);
    }
  }
  throw lastErr; // unreachable — loop either returns or throws
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Where an upload goes. `conversationId`: a file dropped into that chat (AW-122 U2), kept in it
 * until it goes into his data; the server binds it only if the conversation is the caller's.
 * Omitted (every Documents-page caller): a workspace document, exactly as before.
 */
export interface UploadScope {
  conversationId?: string;
}

/** The scope's request fields: nothing at all for a workspace upload (the body is unchanged). */
function scopeFields(scope?: UploadScope): { conversation_id?: string } {
  return scope?.conversationId ? { conversation_id: scope.conversationId } : {};
}

/** Step 1: Initiate upload — dedup check + presigned URL + DB record. */
export async function initiateUpload(
  filename: string,
  contentType: string,
  fileSizeBytes: number,
  contentHash: string,
  docClass?: string,
  uploadBatchId?: string,
  scope?: UploadScope,
): Promise<InitiateUploadResponse | BlobReusedResponse | DuplicateFileResponse> {
  const { data } = await api.post('/api/v1/files/initiate-upload', {
    filename,
    content_type: contentType,
    file_size_bytes: fileSizeBytes,
    content_hash: contentHash,
    doc_class: docClass ?? 'unclassified',
    // One id per upload action; omitted → the server mints a batch of one.
    upload_batch_id: uploadBatchId ?? null,
    ...scopeFields(scope),
  });
  return data;
}

/**
 * Step 2: Upload file bytes directly to GCS via presigned URL.
 *
 * Carries a *stall* watchdog: the timer is re-armed on every upload-progress
 * event, so a slow-but-progressing upload is fine, but a connection that goes
 * silent for GCS_PUT_STALL_TIMEOUT_MS is aborted and surfaced as a transient
 * error (so the caller can retry instead of the queue slot hanging forever).
 */
export async function uploadToGCS(
  uploadUrl: string,
  file: File,
  onProgress?: (percentage: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;

    const clearStall = () => {
      if (stallTimer !== undefined) {
        clearTimeout(stallTimer);
        stallTimer = undefined;
      }
    };
    const armStall = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        stalled = true;
        xhr.abort();
      }, GCS_PUT_STALL_TIMEOUT_MS);
    };

    const onSignalAbort = () => xhr.abort();
    const finish = () => {
      clearStall();
      signal?.removeEventListener('abort', onSignalAbort);
    };

    xhr.open('PUT', uploadUrl, true);
    // Echo the exact Content-Type the presigned URL was signed with.
    xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (event) => {
      armStall(); // forward progress proves the connection is alive
      if (onProgress && event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      finish();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new UploadError(`GCS upload failed with status ${xhr.status}`, {
            status: xhr.status,
            transient: xhr.status >= 500 || xhr.status === 429,
          }),
        );
      }
    };

    xhr.onerror = () => {
      finish();
      reject(new UploadError('Network error during GCS upload', { transient: true }));
    };

    xhr.onabort = () => {
      finish();
      if (stalled) {
        reject(new UploadError('Upload stalled — no progress', { transient: true }));
      } else {
        reject(new DOMException('Upload aborted', 'AbortError'));
      }
    };

    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', onSignalAbort, { once: true });
    }

    armStall(); // also guard the connection-establishment phase
    xhr.send(file);
  });
}

/** Step 3: Confirm upload — tell backend the file reached GCS. */
export async function confirmUpload(uploadId: string, scope?: UploadScope): Promise<FileUpload> {
  const { data } = await api.post('/api/v1/files/confirm-upload', {
    upload_id: uploadId,
    ...scopeFields(scope),
  });
  return data;
}

/**
 * Full upload orchestration: hash → initiate → (GCS PUT + confirm | shortcut).
 *
 * Three branches based on the initiate-upload response:
 * - DuplicateFileResponse: same content + same filename. No row created.
 * - BlobReusedResponse: same content under new filename. Row created;
 *   skip GCS PUT and confirm — backend already published file.uploaded.
 * - InitiateUploadResponse: new content. PUT bytes to upload_url, then confirm.
 *
 * Each network step retries transient failures (see withRetry). The hash is
 * computed once (deterministic), and the PUT re-sends to the same presigned
 * URL — we never re-initiate after a row exists.
 */
export async function uploadFile(
  file: File,
  docClass?: string,
  onProgress?: (percentage: number) => void,
  signal?: AbortSignal,
  precomputedHash?: string,
  uploadBatchId?: string,
  scope?: UploadScope,
): Promise<UploadResult> {
  onProgress?.(0);
  // The caller may pass a hash it already computed (so the "Hashing" UI state
  // can render before this runs); otherwise compute it here.
  const contentHash = precomputedHash ?? (await computeSHA256(file));
  if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');

  const initResult = await withRetry(
    () => initiateUpload(file.name, file.type, file.size, contentHash, docClass, uploadBatchId, scope),
    signal,
  );

  if ('duplicate' in initResult && initResult.duplicate) {
    return { file: initResult.file, isDuplicate: true, bytesReused: false };
  }
  if ('bytes_already_uploaded' in initResult && initResult.bytes_already_uploaded) {
    onProgress?.(100);
    return { file: initResult.file, isDuplicate: false, bytesReused: true };
  }
  if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');

  const { upload_url, file: fileRecord } = initResult as InitiateUploadResponse;

  await withRetry(() => uploadToGCS(upload_url, file, onProgress, signal), signal);
  if (signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');

  const confirmed = await withRetry(() => confirmUpload(fileRecord.id, scope), signal);
  onProgress?.(100);
  return { file: confirmed, isDuplicate: false, bytesReused: false };
}
